// Blob Replacement Cache for reliable replays
import { DefaultAudioStreamer } from './AudioStreamer';
export class TtsAudioPlayer {
    constructor(deps) {
        // Cache for fully buffered audio blobs (for reliable replays)
        this.blobCache = new Map();
        this.fetchTts = deps.fetchTts;
        this.createObjectUrl =
            deps.createObjectUrl ?? ((blob) => URL.createObjectURL(blob));
        this.streamer = deps.streamer ?? new DefaultAudioStreamer({ createObjectUrl: this.createObjectUrl });
    }
    getCacheKey(text, options) {
        const parts = [
            text ?? '',
            options?.voice ?? '',
            options?.language ?? '',
            options?.mimeType ?? 'audio/mpeg',
            options?.instructions ?? '',
            options?.model ?? ''
        ];
        return parts.join('|');
    }
    /**
     * Stream TTS audio and cache the resulting audio blob for reliable replays.
     * - First call: returns a MediaSource-backed URL for immediate playback, while buffering chunks
     * - After completion: stores a Blob in an internal cache for future replays
     * - Subsequent calls: returns a fresh blob: URL created from cached blob data (no re-streaming)
     */
    async getStreamingUrlWithBlobCache(text, options) {
        const cacheKey = this.getCacheKey(text, options);
        // Cleanup expired entries
        const now = Date.now();
        for (const [key, value] of this.blobCache.entries()) {
            if (now - value.timestamp > TtsAudioPlayer.BLOB_TTL_MS) {
                this.blobCache.delete(key);
            }
        }
        // Serve from blob cache if available
        const cached = this.blobCache.get(cacheKey);
        if (cached) {
            return this.createObjectUrl(cached.blob);
        }
        const result = await this.fetchTts(text, options);
        // If we received a pre-buffered result, store and return blob URL immediately
        if (!this.isReadableStream(result)) {
            const mime = options?.mimeType ?? 'audio/mpeg';
            const blob = new Blob([result], { type: mime });
            this.blobCache.set(cacheKey, { blob, timestamp: Date.now() });
            return this.createObjectUrl(blob);
        }
        // Streaming path
        const MediaSourceCtor = typeof window !== 'undefined' ? window.MediaSource : undefined;
        const isMediaSourceApiSupported = !!MediaSourceCtor &&
            typeof MediaSourceCtor.isTypeSupported === 'function' &&
            MediaSourceCtor.isTypeSupported(options?.mimeType ?? 'audio/mpeg');
        // iPadOS reports MediaSource as supported, but streaming playback is choppy/broken.
        // iPadOS 13+ identifies as "Macintosh" in userAgent, so detect via touch support.
        // NOTE: This may produce false positives for desktop Safari with touch devices (e.g., drawing tablets),
        // but the performance impact (full buffering vs streaming) is acceptable.
        const isIPadOS = typeof navigator !== 'undefined' &&
            navigator.platform === 'MacIntel' &&
            navigator.maxTouchPoints > 1;
        const useMediaSourceStreaming = isMediaSourceApiSupported && !isIPadOS;
        const reader = result.getReader();
        const collected = [];
        if (!useMediaSourceStreaming) {
            // Fallback: collect entire stream and return blob URL
            while (true) {
                const { done, value } = await reader.read();
                if (done)
                    break;
                if (value)
                    collected.push(value);
            }
            const blob = new Blob(collected, { type: options?.mimeType ?? 'audio/mpeg' });
            this.blobCache.set(cacheKey, { blob, timestamp: Date.now() });
            return this.createObjectUrl(blob);
        }
        const mediaSource = new MediaSource();
        const mediaSourceUrl = URL.createObjectURL(mediaSource);
        let sourceBuffer;
        const processStream = async () => {
            try {
                while (true) {
                    const { done, value } = await reader.read();
                    if (done) {
                        const blob = new Blob(collected, { type: options?.mimeType ?? 'audio/mpeg' });
                        this.blobCache.set(cacheKey, { blob, timestamp: Date.now() });
                        if (mediaSource.readyState === 'open') {
                            try {
                                mediaSource.endOfStream();
                            }
                            catch { }
                        }
                        break;
                    }
                    if (value) {
                        collected.push(value);
                        if (sourceBuffer && !sourceBuffer.updating) {
                            try {
                                sourceBuffer.appendBuffer(value);
                            }
                            catch { }
                        }
                    }
                }
            }
            catch {
                try {
                    mediaSource.endOfStream();
                }
                catch { }
            }
        };
        mediaSource.addEventListener('sourceopen', () => {
            try {
                sourceBuffer = mediaSource.addSourceBuffer(options?.mimeType ?? 'audio/mpeg');
                void processStream();
            }
            catch {
                // Fallback: collect entire stream and cache blob
                (async () => {
                    try {
                        while (true) {
                            const { done, value } = await reader.read();
                            if (done)
                                break;
                            if (value)
                                collected.push(value);
                        }
                        const blob = new Blob(collected, { type: options?.mimeType ?? 'audio/mpeg' });
                        this.blobCache.set(cacheKey, { blob, timestamp: Date.now() });
                    }
                    catch { }
                    try {
                        mediaSource.endOfStream();
                    }
                    catch { }
                })();
            }
        });
        mediaSource.addEventListener('error', () => {
            try {
                mediaSource.endOfStream();
            }
            catch { }
        });
        return mediaSourceUrl;
    }
    clearBlobCacheByText(text, options) {
        const cacheKey = this.getCacheKey(text, options);
        this.blobCache.delete(cacheKey);
    }
    isReadableStream(value) {
        return (typeof value === 'object' &&
            value !== null &&
            typeof value.getReader === 'function');
    }
}
TtsAudioPlayer.BLOB_TTL_MS = 5 * 60 * 1000; // 5 minutes
